import { isConfigured, dbGetUser, dbUpsertUser } from './supabase'

const KEY         = 'fairfares_profile'
const SESSION_KEY = 'fairfares_session'

/** Read the Supabase JWT from the persisted session (browser-only). */
function getSessionToken() {
  if (typeof window === 'undefined') return null
  try {
    const s = localStorage.getItem(SESSION_KEY)
    return s ? (JSON.parse(s)?.access_token ?? null) : null
  } catch { return null }
}

/**
 * Convert a Supabase users row (snake_case + meta JSONB) to camelCase profile.
 */
function rowToProfile(row) {
  if (!row) return null
  const meta     = row.meta ?? {}
  const airports = row.home_airports ?? meta.homeAirports ?? []
  return {
    ...meta,
    tier:         row.tier          ?? 'free',
    homeAirports: airports,
    homeAirport:  airports[0]       ?? meta.homeAirport ?? 'AMS',
    flexDays:     row.flex_days     ?? 0,
    locale:       row.locale        ?? 'en',
    premiumUntil: row.premium_until ?? null,
    premium:      row.tier === 'premium' || !!meta.premium,
    nationality:  row.nationality   ?? meta.nationality ?? null,
    emailAlerts:     row.email_alerts     ?? meta.emailAlerts ?? true,
    telegramChatId:  row.telegram_chat_id ?? null,
    telegramAlerts:  row.telegram_alerts  ?? 'off',
    stripeCustomerId: row.stripe_customer_id ?? null,
  }
}

/**
 * Convert a camelCase profile object to a Supabase users row.
 * Known fields go to dedicated columns; everything else goes into meta JSONB.
 */
function profileToRow(uid, data) {
  const {
    homeAirports, homeAirport, flexDays, tier, locale,
    premium, premiumUntil, nationality, emailAlerts, telegramChatId, telegramAlerts,
    ...rest
  } = data

  const airports = homeAirports ?? (homeAirport ? [homeAirport] : undefined)
  const row      = { id: uid }

  if (airports     !== undefined) row.home_airports = airports
  if (flexDays     !== undefined) row.flex_days     = flexDays
  if (tier         !== undefined) row.tier          = tier
  if (locale       !== undefined) row.locale        = locale
  if (premiumUntil !== undefined) row.premium_until = premiumUntil
  if (nationality  !== undefined) row.nationality   = nationality
  if (emailAlerts     !== undefined) row.email_alerts     = emailAlerts
  if (telegramChatId !== undefined) row.telegram_chat_id = telegramChatId
  if (telegramAlerts !== undefined) row.telegram_alerts  = telegramAlerts
  row.meta = rest

  return row
}

/**
 * Fetch a user profile from Supabase.
 * Returns null if not configured or no token — never falls back to localStorage.
 * @param {string}      uid
 * @param {string|null} [token]
 */
export async function getUserProfile(uid, token) {
  const tok = token ?? getSessionToken()
  if (!isConfigured() || !tok) return null
  const row = await dbGetUser(uid, tok)
  return rowToProfile(row)
}

/**
 * Write (merge) user preferences to Supabase.
 * Throws if not configured or no token — never falls back to localStorage.
 * @param {string}      uid
 * @param {object}      data
 * @param {string|null} [token]
 */
export async function saveUserProfile(uid, data, token) {
  const tok = token ?? getSessionToken()
  if (!isConfigured() || !tok) throw new Error('Not authenticated')
  const existing = await getUserProfile(uid, tok) ?? {}
  const merged   = { ...existing, ...data }
  await dbUpsertUser(profileToRow(uid, merged), tok)
}

/**
 * Mark a user as waitlisted.
 * @param {string} uid
 * @param {string} email
 */
export async function joinWaitlist(uid, email) {
  await saveUserProfile(uid, { waitlist: true })
  try {
    localStorage.setItem(
      `fairfares_waitlist_${uid}`,
      JSON.stringify({ uid, email, createdAt: new Date().toISOString() })
    )
  } catch {}
}
